import React, { useState, useCallback, useMemo, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Row, Col, Container, Pagination, Form, Button, Modal } from 'react-bootstrap';
import 'bootstrap/dist/css/bootstrap.min.css';
import './News.css';
import { Helmet } from 'react-helmet';
import CardComponent from './CardComponent';
import { checkAndUpdateLanguage } from '../i18n/i18n';

// Import all news data files
import newsDataEn from '../data/newsData_en.json';
import newsDataSc from '../data/newsData_sc.json';
import newsDataTc from '../data/newsData_tc.json';
import newsDataTh from '../data/newsData_th.json';

// Define topic translations between languages
// This mapping helps maintain topic selection when changing languages
const topicTranslations = {
  // English topics as keys, with corresponding translations in other languages
  "Social Impact": {
    en: "Social Impact",
    sc: "社会影响",
    tc: "社會影響",
    th: "ผลกระทบทางสังคม"
  },
  "Technology": {
    en: "Technology",
    sc: "技术",
    tc: "技術",
    th: "เทคโนโลยี"
  },
  "Tech News": {
    en: "Tech News",
    sc: "科技新闻",
    tc: "科技新聞",
    th: "ข่าวเทคโนโลยี"
  },
  "Ethics & Oversight": {
    en: "Ethics & Oversight",
    sc: "伦理与监督",
    tc: "倫理與監督",
    th: "จริยธรรมและการกำกับดูแล"
  },
  "Health Innovation": {
    en: "Health Innovation",
    sc: "健康创新",
    tc: "健康創新",
    th: "นวัตกรรมด้านสุขภาพ"
    
  }
  // Add more topics as needed
};

// Create reverse mappings for each language
const createReverseTopicMapping = () => {
  const reverseMapping = { en: {}, sc: {}, tc: {}, th: {} };

  Object.entries(topicTranslations).forEach(([englishTopic, translations]) => {
    Object.entries(translations).forEach(([lang, translatedTopic]) => {
      reverseMapping[lang][translatedTopic] = englishTopic;
    });
  });

  return reverseMapping;
};

const reverseTopicMapping = createReverseTopicMapping();

const News = () => {
  const { t, i18n } = useTranslation();
  const [newsData, setNewsData] = useState([]);
  const [error, setError] = useState(null);
  const newsPerPage = 4;
  const [currentPage, setCurrentPage] = useState(1);
  const [currentFilter, setCurrentFilter] = useState('All');
  const [previousLanguage, setPreviousLanguage] = useState(i18n.language);
  const [showBackToTop, setShowBackToTop] = useState(false);
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  const [selectedArticle, setSelectedArticle] = useState(null);
  const [showDetails, setShowDetails] = useState(false);

  console.log('News component rendered. Current language:', i18n.language);

  // Function to find equivalent topic in another language
  const getTopicInLanguage = (topic, fromLang, toLang) => {
    if (topic === 'All') return 'All';

    // Convert language code to our format
    fromLang = checkAndUpdateLanguage(fromLang);
    toLang = checkAndUpdateLanguage(toLang);

    // First get the canonical (English) topic name
    const canonicalTopic = reverseTopicMapping[fromLang][topic];

    // If we have a mapping for this topic
    if (canonicalTopic && topicTranslations[canonicalTopic]) {
      // Return the topic in the target language
      return topicTranslations[canonicalTopic][toLang] || topic;
    }

    return topic; // Fallback to original topic if no mapping exists
  };

  const loadNewsData = useCallback(() => {
    try {
      const language = checkAndUpdateLanguage(i18n.language);
      let data;

      console.log('Loading news data for language:', language);

      switch (language) {
        case 'en':
          data = newsDataEn;
          break;
        case 'sc':
          data = newsDataSc;
          break;
        case 'tc':
          data = newsDataTc;
          break;
        case 'th':
          data = newsDataTh;
          break;
        default:
          data = newsDataEn; // fallback to English
      }

      // If we're changing languages and have a current filter other than 'All'
      if (currentFilter !== 'All' && previousLanguage !== language) {
        // Get the equivalent topic in the new language
        const newTopic = getTopicInLanguage(currentFilter, previousLanguage, language);

        // Check if the new topic exists in the data
        const topicExists = data.some(article => article.topic === newTopic);

        // Update the current filter if the translated topic exists
        if (topicExists) {
          setCurrentFilter(newTopic);
        } else {
          // Fallback to 'All' if the topic doesn't exist in this language
          setCurrentFilter('All');
        }
      }

      // Update previous language for next change
      setPreviousLanguage(language);

      console.log('Data loaded:', data);
      const enrichedData = Array.isArray(data)
        ? data.map(article => ({ ...article, details: article.details || article.text }))
        : [];
      setNewsData(enrichedData);
      setCurrentPage(1); // Reset to first page
      setError(null);
    } catch (err) {
      console.error('Error loading news data:', err);
      setError(err);
    }
  }, [i18n.language, currentFilter, previousLanguage]);

  useEffect(() => {
    console.log('useEffect triggered');
    loadNewsData();

    const handleLanguageChange = () => {
      console.log('Language change event detected');
      loadNewsData();
    };

    const handleScroll = () => {
      setShowBackToTop(window.pageYOffset > 300);
    };

    const handleResize = () => {
      setWindowWidth(window.innerWidth);
    };

    window.addEventListener('languageChanged', handleLanguageChange);
    window.addEventListener('scroll', handleScroll);
    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('languageChanged', handleLanguageChange);
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('resize', handleResize);
    };
  }, [i18n.language, loadNewsData]);

  const handleFilterChange = useCallback((event) => {
    setCurrentFilter(event.target.value);
    setCurrentPage(1);
  }, []);
  
  const handleCardClick = useCallback((article) => {
    setSelectedArticle(article);
    setShowDetails(true);
  }, []);
  
  const handleCloseDetails = useCallback(() => {
    setShowDetails(false);
    setSelectedArticle(null);
  }, []);

  const filteredNewsArticles = useMemo(() => {
    if (!newsData || newsData.length === 0) {
      return [];
    }
    return currentFilter === 'All'
      ? newsData
      : newsData.filter((article) => article.topic === currentFilter);
  }, [currentFilter, newsData]);

  const pageCount = Math.ceil(filteredNewsArticles.length / newsPerPage);
  const currentNews = filteredNewsArticles.slice(
    (currentPage - 1) * newsPerPage,
    currentPage * newsPerPage
  );

  const handlePageClick = useCallback((pageNumber) => {
    setCurrentPage(pageNumber);
    // Scroll to the top of the news section when changing pages
    window.scrollTo({
      top: document.querySelector('.news-container').offsetTop,
      behavior: 'smooth'
    });
  }, []);

  // New functions for first and last page navigation
  const goToFirstPage = useCallback(() => {
    setCurrentPage(1);
    // Scroll to the top of the news section when changing pages
    window.scrollTo({
      top: document.querySelector('.news-container').offsetTop,
      behavior: 'smooth'
    });
  }, []);

  const goToLastPage = useCallback(() => {
    setCurrentPage(pageCount);
    // Scroll to the top of the news section when changing pages
    window.scrollTo({
      top: document.querySelector('.news-container').offsetTop,
      behavior: 'smooth'
    });
  }, [pageCount]);

  // Function to determine how many page numbers to show based on screen size
  const getMaxVisiblePages = useCallback(() => {
    if (windowWidth < 480) {
      return 3; // Changed from 1 to 3 for very small screens
    } else if (windowWidth < 768) {
      return 3; // On mobile screens, show 3 pages
    } else {
      return 5; // On larger screens, show 5 pages
    }
  }, [windowWidth]);

  const paginationItems = useMemo(() => {
    let items = [];
    
    // For small screens, we might limit what we show
    const isVerySmall = windowWidth < 400;
    const maxVisiblePages = getMaxVisiblePages();
    
    // Add First Page Button (hidden on very small screens)
    if (!isVerySmall) {
      items.push(
        <Pagination.First 
          key="first" 
          onClick={goToFirstPage} 
          disabled={currentPage === 1}
          aria-label="Go to first page"
          className="golden-pagination"
        />
      );
    }
    
    // Add Previous Button
    items.push(
      <Pagination.Prev 
        key="prev" 
        onClick={() => currentPage > 1 && handlePageClick(currentPage - 1)} 
        disabled={currentPage === 1}
        aria-label="Go to previous page"
        className="golden-pagination"
      />
    );
    
    // Add page number buttons
    let startPage = 1;
    let endPage = pageCount;
    
    if (pageCount > maxVisiblePages) {
      // Calculate start and end pages to display
      const halfVisible = Math.floor(maxVisiblePages / 2);
      
      if (currentPage <= halfVisible + 1) {
        // At the beginning, show first maxVisiblePages
        endPage = maxVisiblePages;
      } else if (currentPage >= pageCount - halfVisible) {
        // At the end, show last maxVisiblePages
        startPage = Math.max(1, pageCount - maxVisiblePages + 1);
      } else {
        // In the middle, show currentPage and surrounding pages
        startPage = currentPage - halfVisible;
        endPage = currentPage + halfVisible;
      }
      
      // Add ellipsis at the beginning if needed
      if (startPage > 1 && !isVerySmall) {
        items.push(
          <Pagination.Ellipsis key="ellipsis-start" disabled className="golden-pagination d-none d-sm-block" />
        );
      }
    }
    
    // Add page number items - always display at least 3 pages regardless of screen size
    for (let number = startPage; number <= endPage; number++) {
      items.push(
        <Pagination.Item
          key={number}
          active={number === currentPage}
          onClick={() => handlePageClick(number)}
          aria-label={`Go to page ${number}`}
          className="golden-pagination"
        >
          {number}
        </Pagination.Item>
      );
    }
    
    // Add ellipsis at the end if needed
    if (endPage < pageCount && !isVerySmall) {
      items.push(
        <Pagination.Ellipsis key="ellipsis-end" disabled className="golden-pagination d-none d-sm-block" />
      );
    }
    
    // Add Next Button
    items.push(
      <Pagination.Next 
        key="next" 
        onClick={() => currentPage < pageCount && handlePageClick(currentPage + 1)} 
        disabled={currentPage === pageCount}
        aria-label="Go to next page"
        className="golden-pagination"
      />
    );
    
    // Add Last Page Button (hidden on very small screens)
    if (!isVerySmall) {
      items.push(
        <Pagination.Last 
          key="last" 
          onClick={goToLastPage} 
          disabled={currentPage === pageCount}
          aria-label="Go to last page"
          className="golden-pagination"
        />
      );
    }
    
    return items;
  }, [pageCount, currentPage, handlePageClick, goToFirstPage, goToLastPage, windowWidth, getMaxVisiblePages]);

  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  };

  if (error) {
    return <div>Error: {error.message}</div>;
  }

  return (
    <>
      <Helmet>
        <meta charSet="utf-8" />
        <meta name="viewport" content="width=device-width, initial-scale=1" />
        <meta name="theme-color" content="#000000" />
        <title>{t('phoenix.ai - A.I. Social Impact News')}</title>
        <meta name="description" content={t('Stay up-to-date with the latest AI technology news , covering social impact, innovation, robotics, and more.')} />
        <link rel="canonical" href="https://www.phoenix.ai/news" />

        {/* Additional meta tags for better SEO */}
        <meta name="keywords" content="AI news, AI for Good, Let A.I. benefit society., artificial intelligence, social impact, technology news, robotics" />
        <meta name="author" content="phoenix.ai" />
        <meta name="robots" content="index, follow" />

        {/* OpenGraph tags - Enhanced */}
        <meta property="og:title" content={t('phoenix.ai - Latest AI News and Social Impact')} />
        <meta property="og:description" content={t('Stay up-to-date with the latest technology news , covering AI innovations, social impact, robotics, and more.')} />
        <meta property="og:image" content="https://www.phoenix.ai/background.jpg" />
        <meta property="og:url" content="https://www.phoenix.ai/news" />
        <meta property="og:type" content="website" />
        <meta property="og:site_name" content="phoenix.ai" />
        <meta property="og:locale" content={i18n.language} />

        {/* Twitter Card tags */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={t('phoenix.ai - Latest AI News and Social Impact')} />
        <meta name="twitter:description" content={t('Stay up-to-date with the latest technology news , covering AI innovations, social impact, robotics, and more.')} />
        <meta name="twitter:image" content="https://www.phoenix.ai/background.jpg" />

        <link rel="apple-touch-icon" href="https://www.phoenix.ai/background.jpg" />
        <link rel="manifest" href="%PUBLIC_URL%/manifest.json" />

        {/* Enhanced JSON-LD structured data for news collection */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "CollectionPage",
            "headline": t('phoenix.ai - A.I. Social Impact News'),
            "description": t('Stay up-to-date with the latest AI technology news , covering social impact, innovation, robotics, and more.'),
            "publisher": {
              "@type": "Organization",
              "name": "phoenix.ai",
              "logo": {
                "@type": "ImageObject",
                "url": "https://www.phoenix.ai/logo.png"
              }
            },
            "url": "https://www.phoenix.ai/news",
            "mainEntity": {
              "@type": "ItemList",
              "itemListElement": currentNews.map((article, index) => ({
                "@type": "ListItem",
                "position": index + 1,
                "url": `https://www.phoenix.ai/news/${article.id}`,
                "name": article.title,
                "description": article.text.substring(0, 200)
              }))
            }
          })}
        </script>
      </Helmet>

      <Container fluid className="news-container">
        <div className="separator" />
        <div className="separator" />
        <h1 className="text-left responsive-heading">{t('A.I. Social Impact')}</h1>
        <div className="separator" />

        <Row className="align-items-center justify-content-end">
          <Col xs={12} sm={4} md={3} lg={2} className="mb-2 pe-3">
            <Form>
              <Form.Group controlId="topicFilter">
                <Form.Label className="visually-hidden">Filter by topic</Form.Label>
                <Form.Control
                  as="select"
                  size="sm"
                  value={currentFilter}
                  onChange={handleFilterChange}
                  className="form-select-sm custom-select"
                >
                  <option value="All">{t('All Topics')}</option>
                  {newsData && [...new Set(newsData.map((article) => article.topic))].map((topic) => (
                    <option key={topic} value={topic}>
                      {topic}
                    </option>
                  ))}
                </Form.Control>
              </Form.Group>
            </Form>
          </Col>
        </Row>
        <br />
        <br />
        <br />

        <Row className="news-container">
          {currentNews.length > 0 ? (
            currentNews.map((article) => (
              <Col xs={12} sm={6} md={4} lg={3} className="mb-4" key={article.title}>
                <CardComponent
                  className="card-hover"
                  image={article.image}
                  date={article.date}
                  title={article.title}
                  text={article.text}
                  photoCredit={article.photoCredit}
                  url={article.url}
                  alt={`${article.title} - ${article.text.substring(0, 100)}...`}
                  topic={article.topic}
                  credit={article.Credit}
                  onClick={() => handleCardClick(article)}
                />
                {/* JSON-LD structured data for individual article */}
                <script type="application/ld+json">
                  {JSON.stringify({
                    "@context": "https://schema.org",
                    "@type": "NewsArticle",
                    "headline": article.title,
                    "datePublished": article.date,
                    "description": article.text.substring(0, 200),
                    "image": article.image,
                    "url": `https://www.phoenix.ai/news/${article.id}`
                  })}
                </script>
              </Col>
            ))
          ) : (
            <Col xs={12} className="text-center my-5">
              <h3>{t('No news found for the selected topic')}</h3>
            </Col>
          )}
        </Row>
        <br />
        <br />

        {pageCount > 0 && (
          <Row>
            <Col>
              <Pagination className="justify-content-center my-pagination">{paginationItems}</Pagination>
            </Col>
          </Row>
        )}
        <div className="separator" />
      </Container>

      <Modal show={showDetails} onHide={handleCloseDetails} size="lg" centered>
        <Modal.Header>
          <Modal.Title>{selectedArticle?.title}</Modal.Title>
          <button type="button" className="custom-modal-close" aria-label="Close" onClick={handleCloseDetails}>×</button>
        </Modal.Header>
        <Modal.Body>
          {selectedArticle && (
            <CardComponent
              image={selectedArticle.image}
              date={selectedArticle.date}
              title={selectedArticle.title}
              text={selectedArticle.details || selectedArticle.text}
              photoCredit={selectedArticle.photoCredit}
              url={selectedArticle.url}
              alt={`${selectedArticle.title} - ${(selectedArticle.details || selectedArticle.text || '').substring(0, 100)}...`}
              topic={selectedArticle.topic}
              credit={selectedArticle.Credit}
            />
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button className="modal-close-btn" onClick={handleCloseDetails} aria-label="Close details">
            Close
          </Button>
        </Modal.Footer>
      </Modal>

      {showBackToTop && (
        <Button
          onClick={scrollToTop}
          className="back-to-top"
          variant="primary"
          aria-label="Back to top"
        >
          ↑
        </Button>
      )}

      {/* Additional styles for golden pagination and responsive heading */}
      <style>
        {`
          /* Responsive heading styles - NEW */
          .responsive-heading {
            font-size: 2.5rem;
          }
          
          @media (max-width: 991px) {
            .responsive-heading {
              font-size: 2.2rem;
            }
          }
          
          @media (max-width: 767px) {
            .responsive-heading {
              font-size: 1.8rem;
            }
          }
          
          @media (max-width: 575px) {
            .responsive-heading {
              font-size: 1.5rem;
            }
          }
          
          @media (max-width: 400px) {
            .responsive-heading {
              font-size: 1.2rem;
            }
          }
          
          /* Golden pagination styles */
          .my-pagination .page-item .page-link {
            color: rgba(212, 163, 115, 0.8);
            border: 1px solid rgba(225, 166, 106, 0.85);
            border-radius: 4px;
            margin: 0 2px;
            transition: all 0.3s ease;
            background-color: rgba(250, 235, 215, 0.7); /* Light golden background */
          }
          
          .my-pagination .page-item.active .page-link {
            background-color: rgba(212, 163, 115, 0.8) !important;
            border-color: rgba(212, 163, 115, 1);
            color: #fff !important;
          }
          
          .my-pagination .page-item .page-link:hover {
            background-color: rgba(225, 160, 95, 0.6);
            border-color: rgba(212, 163, 115, 0.8);
            color: #fff;
          }
          
          .my-pagination .page-item.active .page-link:hover {
            background-color: rgba(212, 163, 115, 0.9) !important;
          }
          
          .my-pagination .page-item.disabled .page-link {
            color: rgba(212, 163, 115, 0.67);
            border-color: rgba(211, 141, 71, 0.66);
            background-color: rgba(250, 235, 215, 0.4); /* Lighter background for disabled */
          }
          
          /* Better focus styles for accessibility */
          .my-pagination .page-item .page-link:focus {
            box-shadow: 0 0 0 0.2rem rgba(203, 121, 40, 0.72);
            outline: none;
          }
          
          /* First, Last, Next, Prev buttons with more golden look */
          .my-pagination .page-item:first-child .page-link,
          .my-pagination .page-item:last-child .page-link,
          .my-pagination .page-item .page-link[aria-label="Go to first page"],
          .my-pagination .page-item .page-link[aria-label="Go to last page"],
          .my-pagination .page-item .page-link[aria-label="Go to next page"],
          .my-pagination .page-item .page-link[aria-label="Go to previous page"] {
            background-color: rgba(219, 172, 105, 0.3);
            color: rgba(165, 113, 55, 0.9);
            font-weight: bold;
          }
          
          .my-pagination .page-item:first-child .page-link:hover,
          .my-pagination .page-item:last-child .page-link:hover,
          .my-pagination .page-item .page-link[aria-label="Go to first page"]:hover,
          .my-pagination .page-item .page-link[aria-label="Go to last page"]:hover,
          .my-pagination .page-item .page-link[aria-label="Go to next page"]:hover,
          .my-pagination .page-item .page-link[aria-label="Go to previous page"]:hover {
            background-color: rgba(212, 163, 115, 0.7);
            color: #fff;
          }
          
          /* Ellipsis styling */
          .my-pagination .page-item .page-link.disabled {
            background-color: transparent;
          }
          
          /* Responsive pagination styles */
          @media (max-width: 767px) {
            .my-pagination {
              margin-top: 1rem;
              margin-bottom: 1rem;
            }
            
            .my-pagination .page-item .page-link {
              padding: 0.4rem 0.65rem;
              font-size: 0.9rem;
            }
          }
          
          @media (max-width: 575px) {
            .my-pagination .page-item .page-link {
              padding: 0.35rem 0.6rem;
              font-size: 0.85rem;
              margin: 0 1px;
            }
          }
          
          @media (max-width: 399px) {
            .my-pagination .page-item .page-link {
              padding: 0.3rem 0.5rem;
              font-size: 0.8rem;
              margin: 0;
            }
            
            /* On very small screens, give more importance to prev/next buttons */
            .my-pagination .page-item .page-link[aria-label="Go to previous page"],
            .my-pagination .page-item .page-link[aria-label="Go to next page"] {
              padding: 0.3rem 0.6rem;
            }
          }
          
          /* Make sure pagination is contained and scrollable on very small screens */
          @media (max-width: 350px) {
            .my-pagination {
              max-width: 100%;
              overflow-x: auto;
              justify-content: flex-start !important;
              padding-bottom: 0.5rem;
            }
            
            .my-pagination .pagination {
              flex-wrap: nowrap;
            }
          }
        `}
      </style>
    </>
  );
};

export default News;
